import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { withRouter, useHistory } from "react-router-dom/cjs/react-router-dom";

import {
  SPACE,
  ROUTES,
  messages,
  EMPTY_STRING,
  localStorageKeys,
  customerConstants,
  tipYourShutterb,
  eventBookingConstants,
  eventBookingStatus,
} from "../../../../../../../constants";
import { ReactComponent as CartShopIcon } from "../../../../../../../assets/icons/cart-shop.svg";
import {
  NavigateToPath,
  saveToLocalStorage,
} from "../../../../../../../system";
import {
  customToast,
  normalizeValue,
  normalizeEventDates,
} from "../../../../../../../shared/utility";
import SelectField from "../../../../../../../shared/components/Fields/SelectField";
import SectionHeader from "../../../../../../../shared/components/Events/SectionHeader";
import ConfirmationModal from "../../../../../../../shared/components/Modal/ConfirmationModal";
import {
  eventBookingActions,
  customerDashboardActions,
} from "../../../../../../../redux/actions";

const ExtendYourSb = (props) => {
  const history = useHistory();
  const { groupEventId, eventId } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventOrderItems, setEventOrderItems] = useState([]);
  const [isCheckoutClick, setIsCheckoutClick] = useState(null);

  useEffect(() => {
    if (groupEventId) {
      props.getAllEventsByGroupId(groupEventId);
    }
    // eslint-disable-next-line
  }, [groupEventId]);

  useEffect(() => {
    if (props.allEvents.length && eventId) {
      setSelectedEvent(props.allEvents?.find((event) => event.id === eventId));
    }
    // eslint-disable-next-line
  }, [props.allEvents, eventId]);

  useEffect(() => {
    if (selectedEvent) {
      props.getExtendedEventDetails(selectedEvent.id);
    }
    // eslint-disable-next-line
  }, [selectedEvent]);

  useEffect(() => {
    if (selectedEvent && props?.myEventsExtendSb?.orderItems && eventId) {
      const eventId = selectedEvent.id;
      const event = props.myEventsExtendSb;

      const existingEvent = props.customerDashboardCart.find(
        (e) => e.id === eventId
      );

      const updatedItems = event.orderItems.map((item) => {
        const existingExtend = existingEvent?.orderItems.find(
          (s) =>
            (s.id === item.id || s.orderItemId === item.id) &&
            s.status === eventBookingStatus.EXTENDED
        );

        return existingExtend
          ? { ...item, ...existingExtend, status: eventBookingStatus.EXTENDED }
          : {
              ...item,
              status: eventBookingStatus.EXTENDED,
              extendedPrice_: item?.sbUser?.hourlyRate ?? 0,
              extendedQuantity_: 0,
              isExtendSelected: false,
            };
      });

      const updatedState = {
        ...event,
        orderItems: updatedItems,
      };

      setEventOrderItems((prev) => {
        const filtered = prev.filter((e) => e.id !== eventId);
        return [...filtered, updatedState];
      });
    }
    // eslint-disable-next-line
  }, [props?.myEventsExtendSb, selectedEvent]);

  // function to handle the events dropdown
  const handleEventSelect = (e) => {
    setSelectedEvent(e);
  };

  let checkoutTimeout;
  const handleClick = (e, isCheckoutClick, isUpdateCart) => {
    e.preventDefault();

    if (checkoutTimeout) return;
    checkoutTimeout = setTimeout(() => (checkoutTimeout = null), 2000);

    setIsCheckoutClick(isCheckoutClick);
    handleExtendSubmit(false, isCheckoutClick, isUpdateCart);
  };

  const handleExtendSubmit = async (
    closeModal,
    isCheckoutClick,
    isUpdateCart = false
  ) => {
    const eventId = selectedEvent?.id;
    if (!eventId) return;
    const currentEvent = eventOrderItems.find((ev) => ev.id === eventId);
    if (!currentEvent) return;

    const selectedExtendItems = currentEvent.orderItems.filter(
      (sb) => sb.status === eventBookingStatus.EXTENDED && sb.isExtendSelected
    );
    const allValid = selectedExtendItems.every(
      (sb) => sb.extendedQuantity_ > 0
    );
    const cartHasItems = (props.customerDashboardCart?.length ?? 0) > 0;

    const isCheckoutFlow = isCheckoutClick === tipYourShutterb.CHECKOUT;
    const noSelection = !allValid || selectedExtendItems.length === 0;

    if (!isCheckoutFlow) {
      // Add to cart flow
      if ((isUpdateCart && !allValid) || (!isUpdateCart && noSelection)) {
        customToast(
          messages.PLEASE_SELECT_ATLEAST_1_SHUTTERB_TO_EXTEND,
          toast.TYPE.ERROR
        );
        return;
      }
    } else if (!cartHasItems && noSelection) {
      // Checkout flow
      customToast(
        messages.PLEASE_SELECT_ATLEAST_1_SHUTTERB_TO_EXTEND,
        toast.TYPE.ERROR
      );
      return;
    }

    // Updated event with extend items
    const updatedEvent = {
      ...currentEvent,
      orderItems: selectedExtendItems,
    };

    // Existing cart
    let updatedCart = [...(props.customerDashboardCart || [])];

    // Check if event already exists in cart
    const existingIndex = updatedCart.findIndex((ev) => ev.id === eventId);

    if (selectedExtendItems.length > 0) {
      if (existingIndex > -1) {
        const existingEvent = updatedCart[existingIndex];

        // merge extend + existing tip
        const tipItems = existingEvent.orderItems.filter(
          (sb) => sb.status === eventBookingStatus.TIP
        );

        updatedCart[existingIndex] = {
          ...existingEvent,
          orderItems: [...tipItems, ...selectedExtendItems],
        };
      } else {
        // add new with only extend items
        updatedCart.push(updatedEvent);
      }
    } else {
      // If no extend items selected
      if (existingIndex > -1) {
        const existingEvent = updatedCart[existingIndex];

        // keep only tip items
        const tipItems = existingEvent.orderItems.filter(
          (sb) => sb.status === eventBookingStatus.TIP
        );

        if (tipItems.length > 0) {
          updatedCart[existingIndex] = {
            ...existingEvent,
            orderItems: tipItems,
          };
        } else {
          // no tip items left -> remove whole event
          updatedCart.splice(existingIndex, 1);
        }
      }
    }

    if (updatedCart.length === 0 && !isUpdateCart) {
      customToast(
        messages.PLEASE_SELECT_ATLEAST_1_SHUTTERB_TO_EXTEND,
        toast.TYPE.ERROR
      );
      return;
    }

    if (props.eventAlreadyExist && !closeModal) {
      setShowModal(true);
      return;
    }

    await props.resetEventBooking(true);

    const updatedFormState = normalizeEventDates(updatedCart);
    props.setFormState(updatedFormState);
    props.addToCustomerDashboardCart(updatedFormState);
    props.setBillingDetails(updatedCart?.[0]?.billingDetails ?? null);

    saveToLocalStorage(
      localStorageKeys.ACTIVE_TAB,
      eventBookingConstants.TABS_ID.CART
    );
    saveToLocalStorage(customerConstants.NOT_NEW_EVENT, true);

    if (
      localStorage.getItem(customerConstants.CART_PREVIOUS_SCREEN) ===
      customerConstants.TIP_TO_CART
    ) {
      localStorage.setItem(
        customerConstants.CART_PREVIOUS_SCREEN,
        customerConstants.EXTEND_AND_TIP_TO_CART
      );
    } else if (
      localStorage.getItem(customerConstants.CART_PREVIOUS_SCREEN) !==
      customerConstants.EXTEND_AND_TIP_TO_CART
    ) {
      localStorage.setItem(
        customerConstants.CART_PREVIOUS_SCREEN,
        customerConstants.EXTEND_TO_CART
      );
    }
    if (isCheckoutClick === tipYourShutterb.CHECKOUT) {
      setIsCheckoutClick(null);
      NavigateToPath(history, ROUTES.EVENT_BOOKING);
    } else {
      customToast(messages.CART_UPDATED_SUCCESSFULLY, toast.TYPE.SUCCESS);
    }
  };

  const updateOrderItems = (eventId, updater) => {
    setEventOrderItems((prev) =>
      prev.map((event) =>
        event.id === eventId
          ? {
              ...event,
              orderItems: event.orderItems.map(updater),
            }
          : event
      )
    );
  };

  const handleCheckboxChange = (eventId, index, checked) => {
    updateOrderItems(eventId, (item, idx) =>
      idx === index ? { ...item, isExtendSelected: checked } : item
    );
  };

  const handleDurationChange = (eventId, index, value) => {
    updateOrderItems(eventId, (item, idx) =>
      idx === index ? { ...item, extendedQuantity_: value } : item
    );
  };

  const isInCart = props.customerDashboardCart?.some(
    (ev) =>
      ev.id === selectedEvent?.id &&
      ev.orderItems?.some((item) => item.status === eventBookingStatus.EXTENDED)
  );

  return (
    <>
      <div className="container my-events-accordions tip-shutterb-wrapper px-0">
        {props.allEvents?.length > 0 && (
          <SectionHeader
            eventId={eventId}
            groupId={groupEventId}
            itemCount={props.allEvents.length || 0}
            isGroup={props.allEvents.length > 1}
            title={
              props.allEvents.length > 1
                ? props.allEvents[0]?.eventGroupName
                : props.allEvents[0]?.eventName
            }
            className="px-4"
          />
        )}
        <div className="px-2 py-2">
          {props.allEvents?.length > 1 && (
            <div className="col-md-6 col-12">
              <SelectField
                options={props.allEvents ?? []}
                id="select-event"
                label="select an event:"
                placeholder="select an event"
                labelKey="eventName"
                valueKey="id"
                value={selectedEvent}
                setValue={handleEventSelect}
              />
            </div>
          )}
          <div className="row my-3">
            <div className="col-12 mb-4">
              <p className="p-0 m-0 mb-1 text fw-600 text-dark clr-secondary">
                {messages.EXTEND_YOUR_SHUTTERB}
              </p>
              <p className="mb-0 pr-3 text-content">
                please select which shutterb you wish to extend. you have the
                option to extend by 30 mins or 1 hour.
              </p>
            </div>

            {selectedEvent && eventOrderItems?.length ? (
              eventOrderItems
                .find((ev) => ev.id === selectedEvent.id)
                ?.orderItems.filter(
                  (sb) => sb.status === eventBookingStatus.EXTENDED
                )
                .map((sb, index, originalArray) => {
                  const isLastIndex = index !== originalArray.length - 1;
                  return (
                    <React.Fragment key={index}>
                      <div className="col-12 col-md-4 col-xl-3 d-flex align-items-center">
                        <input
                          type="checkbox"
                          name="allEventCheckbox"
                          disabled={sb?.sbUser?.isExtended}
                          checked={sb.isExtendSelected}
                          onChange={(e) =>
                            handleCheckboxChange(
                              selectedEvent.id,
                              index,
                              e.target.checked
                            )
                          }
                        />
                        <div className="ml-5 d-flex flex-column align-items-center">
                          <div className="tip-shutterb-profile-image ">
                            <img
                              className={`img-fluid rounded-circle obj-fit-cover w-100 h-100 ${
                                sb?.sbUser?.isExtended
                                  ? "tip-extend-shutterb-image"
                                  : EMPTY_STRING
                              }`}
                              src={
                                process.env.REACT_APP_S3_BASE_URL_PORTFOLIO +
                                "/" +
                                sb?.sbUser?.sbPortfolios[0]?.profileImageUrl
                              }
                              alt="sb-profile"
                            />
                          </div>

                          <p className="p-0 m-0 mt-2 text fw-600 text-dark clr-secondary text-center">
                            {normalizeValue(sb?.sbUser?.firstName) +
                              normalizeValue(SPACE) +
                              normalizeValue(sb?.sbUser?.lastName)}
                          </p>
                        </div>
                      </div>
                      <div className="col-12 col-md-6 align-content-center mt-3 mt-md-0">
                        <SelectField
                          options={window.config?.extendEventDurationOption}
                          id="select-duration"
                          label={`${messages.SELECT_DURATION}`}
                          placeholder={messages.SELECT_DURATION}
                          disabled={
                            !sb.isExtendSelected || sb?.sbUser?.isExtended
                          }
                          value={sb.extendedQuantity_ || EMPTY_STRING}
                          setValue={(data) =>
                            handleDurationChange(
                              selectedEvent.id,
                              index,
                              data?.value
                            )
                          }
                        />
                      </div>
                      <div
                        className={`col-12 ${isLastIndex ? "my-2" : "my-4"}`}
                      >
                        {isLastIndex && <hr className="custom-hr" />}
                      </div>
                    </React.Fragment>
                  );
                })
            ) : (
              <div className="col-12">
                <p className="label-content-600">
                  The event request has not been accepted by any shutterb yet.
                </p>
              </div>
            )}

            <div className="col-12">
              <button
                type="button"
                className="add-to-cart-btn button-common button-outline mb-3 mb-lg-0"
                onClick={(e) =>
                  handleClick(e, tipYourShutterb.ADD_TO_CART, isInCart)
                }
                disabled={!props.myEventsExtendSb}
              >
                <CartShopIcon />
                <span className="pl-1">
                  {isInCart ? messages.UPDATE_CART : messages.ADD_TO_CART}
                </span>
              </button>
              <button
                type="button"
                className="button-common button-fill clr-white ml-3 mb-3 mb-lg-0 font-12"
                onClick={(e) => handleClick(e, tipYourShutterb.CHECKOUT)}
                disabled={!props.myEventsExtendSb}
              >
                {messages.CHECKOUT}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={() => {
          props.setEventAlreadyExist(false);
          setShowModal(false);
          setTimeout(() => {
            handleExtendSubmit(true, isCheckoutClick);
          }, 0);
        }}
        dialogText="are_you_sure_you_want_to_proceed"
        maxWidth="xs"
        buttonText={"proceed"}
      />
    </>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  userId: state.authentication.userId,
  allEvents: state.customerDashboard.allEvents,
  eventAlreadyExist: state.eventBooking.eventAlreadyExist,
  myEventsExtendSb: state.customerDashboard.myEventsExtendSb,
  customerDashboardCart: state.customerDashboard.customerDashboardCart,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getAllEventsByGroupId: (groupEventId) =>
    dispatch(customerDashboardActions.getAllEventsByGroupId(groupEventId)),
  getExtendedEventDetails: (eventId) =>
    dispatch(customerDashboardActions.getExtendedEventDetails(eventId)),
  addToCustomerDashboardCart: (dataObj) =>
    dispatch({
      type: customerConstants.ADD_TO_CUSTOMER_DASHBOARD_CART,
      data: dataObj,
    }),
  setFormState: (data) => dispatch(eventBookingActions.setFormState(data)),
  setBillingDetails: (data) => dispatch(eventBookingActions.setBillingDetails(data)),
  setEventAlreadyExist: (data) =>
    dispatch(eventBookingActions.setEventAlreadyExist(data)),
  resetEventBooking: (isTipOrExtend) =>
    dispatch(eventBookingActions.resetEventBooking(isTipOrExtend)),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ExtendYourSb));
